import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ChartBar, TrendUp, Trophy, Target, Plus, Pencil, Trash, CaretDown, CaretUp, SortDescending, ShareNetwork, UploadSimple, Stack, X, Lock, CalendarBlank } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

// Footer branding
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ShareModal from "../components/ShareModal";
import ImportPaywallModal from "../components/ImportPaywallModal";
import CryptoPaymentModal from "../components/CryptoPaymentModal";
import SharedMonthlyPerformanceSection from "../components/SharedMonthlyPerformanceSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Profit Factor color based on value
const getProfitFactorColor = (pf) => {
  const pfValue = parseFloat(pf) || 0;
  if (pfValue < 1.0) return "text-[#FF3B30]";  // Unprofitable
  if (pfValue <= 1.5) return "text-[#FFC107]"; // Decent
  return "text-[#00E676]"; // Strong
};

// Profit Factor friendly label based on value
const getProfitFactorLabel = (pf) => {
  const pfValue = parseFloat(pf) || 0;
  if (isNaN(pfValue) || pfValue === 0) return "N/A";
  if (pfValue < 1.0) return "Unprofitable";
  if (pfValue <= 1.5) return "Decent";
  return "Strong";
};

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false, trend = null }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={20} weight="duotone" className="text-white/40" />}
    </div>
    <div className="flex items-baseline gap-2">
      <div className={`text-3xl font-bold font-mono ${highlight ? highlight : 'text-white'}`}>
        {value}
      </div>
      {trend !== null && trend.direction !== 'neutral' && (
        <div className={`flex items-center gap-1 text-xs font-mono ${
          trend.direction === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'
        }`}>
          {trend.direction === 'up' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          <span>{trend.delta}</span>
        </div>
      )}
    </div>
    {subtitle && <div className="text-white/60 text-sm mt-1">{subtitle}</div>}
  </div>
);

const getAnalyticsTotalRValue = (analytics) => {
  const candidates = [analytics?.total_rr, analytics?.total_r];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue;
    const numericValue = Number(candidate);
    if (!Number.isNaN(numericValue)) return numericValue;
  }
  return null;
};

const formatAnalyticsTotalR = (analytics) => {
  const value = getAnalyticsTotalRValue(analytics);
  if (value === null) return 'N/A';
  if (value === 0) return '0R';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}R`;
};

const getAnalyticsTotalRColor = (analytics) => {
  const value = getAnalyticsTotalRValue(analytics);
  if (value === null) return 'text-white/40';
  if (value > 0) return 'text-[#00E676]';
  if (value < 0) return 'text-[#FF3B30]';
  return 'text-white';
};

const formatMonthlyAverageValue = (value, suffix, options = {}) => {
  const { signed = false, decimals = 2 } = options;
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const numericValue = Number(value);
  const formatted = numericValue === 0
    ? `0${suffix}`
    : `${signed && numericValue > 0 ? '+' : ''}${numericValue.toFixed(decimals)}${suffix}`;
  return `≈ ${formatted}`;
};

export default function Analytics() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Shared/Public profile mode - when viewing another user's profile
  const { isSharedProfileView, isReadOnly, publicProfileUserId } = useSharedProfile();
  
  // Helper to get the correct route for public vs private mode
  const getRoute = (privatePath) => {
    if (isSharedProfileView && publicProfileUserId) {
      // In public mode, navigate within /profile/{userId}/ namespace
      return `/profile/${publicProfileUserId}${privatePath}`;
    }
    return privatePath;
  };
  
  const [globalAnalytics, setGlobalAnalytics] = useState(null);
  const [setupAnalytics, setSetupAnalytics] = useState([]);
  const [setups, setSetups] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Setup Groups with analytics (for public profile view)
  const [setupGroupsWithAnalytics, setSetupGroupsWithAnalytics] = useState([]);
  
  // Time period filter for public profile
  const [timePeriod, setTimePeriod] = useState('all'); // 'all' | '7' | '30' | '90' | 'custom'
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Direction filter for public profile (All / Long / Short)
  const [directionFilter, setDirectionFilter] = useState('all'); // 'all' | 'long' | 'short'
  
  // All trades for trend calculation
  const [allTrades, setAllTrades] = useState([]);
  
  // Trend comparison mode
  const [trendCompareMode, setTrendCompareMode] = useState('last_10_trades');
  
  // Breakdown data for Trade Type Comparison
  const [breakdown, setBreakdown] = useState(null);
  
  // Setup sorting
  const [setupSortField, setSetupSortField] = useState('trades'); // 'trades' | 'winRate' | 'avgRR' | 'profitFactor'
  const [setupSortDirection, setSetupSortDirection] = useState('desc'); // 'desc' | 'asc' | null
  
  // Setup display mode (collapsed shows featured, expanded shows all)
  const [setupsExpanded, setSetupsExpanded] = useState(false);
  
  // Setup dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingSetup, setEditingSetup] = useState(null);
  const [newSetupName, setNewSetupName] = useState("");
  const [newSetupDescription, setNewSetupDescription] = useState("");
  
  // Asset Group dialogs
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupAssets, setNewGroupAssets] = useState("");
  const [newGroupType, setNewGroupType] = useState("manual");
  const [newGroupExcludedIds, setNewGroupExcludedIds] = useState([]);
  const [manualAddedAssets, setManualAddedAssets] = useState([]);
  const [manualRemovedAssets, setManualRemovedAssets] = useState([]);
  const [newAssetToAdd, setNewAssetToAdd] = useState("");
  
  // Asset Group display - track which groups have expanded asset list
  const [expandedAssetGroups, setExpandedAssetGroups] = useState({});
  const ASSETS_PREVIEW_LIMIT = 5;
  const [currentGroupAssets, setCurrentGroupAssets] = useState([]);
  
  // Share functionality
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if imported data exists (for navigation button)
  const [hasImportedData, setHasImportedData] = useState(false);

  // Paywall for imported analytics (yearly only)
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isUpgradeFlow, setIsUpgradeFlow] = useState(false);
  const [proStatus, setProStatus] = useState({ isPro: false, plan: null });
  
  // Delete All Data (full journal reset)
  const [showDeleteAllWarning, setShowDeleteAllWarning] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAllData, setDeletingAllData] = useState(false);
  
  // Setup Groups
  const [setupGroups, setSetupGroups] = useState([]);
  const [showCreateSetupGroupDialog, setShowCreateSetupGroupDialog] = useState(false);
  const [showEditSetupGroupDialog, setShowEditSetupGroupDialog] = useState(false);
  const [deleteSetupGroupId, setDeleteSetupGroupId] = useState(null);
  const [editingSetupGroup, setEditingSetupGroup] = useState(null);
  const [newSetupGroupName, setNewSetupGroupName] = useState("");
  const [newSetupGroupDescription, setNewSetupGroupDescription] = useState("");
  const [selectedSetupIds, setSelectedSetupIds] = useState([]);

  useEffect(() => {
    fetchData();
    fetchProStatus();
  }, []);
  
  // Refetch data when time period changes (public profile only)
  useEffect(() => {
    if (isSharedProfileView && (timePeriod || customDateRange.start || customDateRange.end || directionFilter)) {
      // Skip initial render
      setLoading(true);
      fetchData();
    }
  }, [timePeriod, customDateRange.start, customDateRange.end, directionFilter, isSharedProfileView]);

  const fetchProStatus = async () => {
    try {
      const response = await api.get('/user/profile');
      const profile = response.data;
      if (profile.subscription_status === 'pro' && profile.subscription_expires_at) {
        const expiresAt = new Date(profile.subscription_expires_at);
        const isActive = expiresAt > new Date();
        setProStatus({
          isPro: isActive,
          plan: profile.subscription_plan // 'monthly' or 'yearly'
        });
      }
    } catch (error) {
      console.error('Error fetching pro status:', error);
    }
  };

  // Handle import button click - check yearly access
  const handleImportClick = () => {
    if (proStatus.plan === 'yearly') {
      navigate('/imported-analytics');
    } else {
      setShowPaywallModal(true);
    }
  };

  // Handle upgrade click from paywall modal - show payment modal with upgrade context
  const handlePaywallUpgradeClick = () => {
    setShowPaywallModal(false);
    setIsUpgradeFlow(true); // Enable upgrade flow for discounted price
    setShowPaymentModal(true);
  };

  // Calculate date range params based on timePeriod
  const getDateParams = () => {
    if (!isSharedProfileView || (timePeriod === 'all' && directionFilter === 'all')) return {};
    
    const now = new Date();
    let startDate = null;
    
    if (timePeriod === '7') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (timePeriod === '30') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (timePeriod === '90') {
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    } else if (timePeriod === 'custom' && customDateRange.start) {
      startDate = customDateRange.start;
    }
    
    const params = {};
    if (startDate) {
      params.start_date = startDate.toISOString();
    }
    if (timePeriod === 'custom' && customDateRange.end) {
      params.end_date = customDateRange.end.toISOString();
    } else if (timePeriod !== 'all') {
      params.end_date = now.toISOString();
    }
    
    // Add direction filter if not 'all'
    if (directionFilter !== 'all') {
      params.direction = directionFilter;
    }
    
    return params;
  };

  const showHistoricalMonthlyAverages = isSharedProfileView
    && timePeriod === 'all'
    && directionFilter === 'all'
    && globalAnalytics?.history_days !== null
    && globalAnalytics?.history_days !== undefined
    && Number(globalAnalytics?.history_days) >= 30
    && globalAnalytics?.history_months;

  const buildMonthlyHistoryUrl = (row) => {
    const params = new URLSearchParams({
      start_date: row.month_start,
      end_date: row.month_end,
      analytics_scope: 'eligible_closed',
    });

    if (directionFilter !== 'all') {
      params.set('direction', directionFilter);
    }

    return `${getRoute('/history')}?${params.toString()}`;
  };

  const fetchData = async () => {
    try {
      const dateParams = getDateParams();
      const queryString = Object.keys(dateParams).length > 0 
        ? '?' + new URLSearchParams(dateParams).toString() 
        : '';
      
      const [globalRes, setupRes, setupsRes, groupsRes, tradesRes, breakdownRes, importedRes, setupGroupsRes, setupGroupsAnalyticsRes] = await Promise.all([
        api.get(`/analytics/global${queryString}`),
        api.get(`/analytics/setups${queryString}`),
        api.get('/setups'),
        api.get('/asset-groups?include_computed=true&include_normalized=true'),
        api.get('/trades'),
        api.get(`/analytics/breakdown${queryString}`),
        api.get('/analytics/imported'),
        api.get('/setup-groups'),
        api.get(`/setup-groups-with-analytics${queryString}`)
      ]);

      setGlobalAnalytics(globalRes.data);
      setSetupAnalytics(setupRes.data);
      setSetups(setupsRes.data);
      setAssetGroups(groupsRes.data);
      setBreakdown(breakdownRes.data);
      setHasImportedData(importedRes.data?.has_imported_data || false);
      setSetupGroups(setupGroupsRes.data || []);
      setSetupGroupsWithAnalytics(setupGroupsAnalyticsRes.data || []);
      
      // Store all trades for trend calculation
      const trades = tradesRes.data;
      setAllTrades(trades);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSetup = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot create setups in view-only mode");
      return;
    }
    
    if (!newSetupName.trim()) {
      toast.error("Please enter a setup name");
      return;
    }

    try {
      await api.post('/setups', {
        name: newSetupName,
        description: newSetupDescription
      });
      
      toast.success("Setup created successfully");
      setNewSetupName("");
      setNewSetupDescription("");
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error creating setup:", error);
      toast.error("Failed to create setup");
    }
  };

  const handleUpdateSetup = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot edit setups in view-only mode");
      return;
    }
    
    if (!newSetupName.trim()) {
      toast.error("Please enter a setup name");
      return;
    }

    try {
      await api.put(`/setups/${editingSetup.id}`, {
        name: newSetupName,
        description: newSetupDescription
      });
      
      toast.success("Setup updated successfully");
      setShowEditDialog(false);
      setEditingSetup(null);
      setNewSetupName("");
      setNewSetupDescription("");
      fetchData();
    } catch (error) {
      console.error("Error updating setup:", error);
      toast.error("Failed to update setup");
    }
  };

  const handleDeleteSetup = async (id) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot delete setups in view-only mode");
      return;
    }
    
    try {
      await api.delete(`/setups/${id}`);
      toast.success("Setup deleted successfully");
      setDeleteId(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting setup:", error);
      toast.error("Failed to delete setup");
    }
  };

  const openEditDialog = (setup) => {
    setEditingSetup(setup);
    setNewSetupName(setup.name);
    setNewSetupDescription(setup.description || "");
    setShowEditDialog(true);
  };

  // Asset Group Management Functions
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    if (newGroupType === "exclude_group" && newGroupExcludedIds.length === 0) {
      toast.error("Please select at least one group to exclude");
      return;
    }

    try {
      const payload = {
        name: newGroupName,
        description: newGroupDescription,
        group_type: newGroupType,
      };

      if (newGroupType === "manual") {
        payload.assets = newGroupAssets
          .split(",")
          .map(a => a.trim().toUpperCase())
          .filter(a => a.length > 0);
      } else if (newGroupType === "exclude_group") {
        payload.excluded_group_ids = newGroupExcludedIds;
      }

      await api.post('/asset-groups', payload);
      
      toast.success("Asset group created successfully");
      resetGroupForm();
      setShowCreateGroupDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error creating asset group:", error);
      toast.error(error.response?.data?.detail || "Failed to create asset group");
    }
  };

  const resetGroupForm = () => {
    setNewGroupName("");
    setNewGroupDescription("");
    setNewGroupAssets("");
    setNewGroupType("manual");
    setNewGroupExcludedIds([]);
    setManualAddedAssets([]);
    setManualRemovedAssets([]);
    setNewAssetToAdd("");
    setCurrentGroupAssets([]);
  };

  const handleUpdateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    if (newGroupType === "exclude_group" && newGroupExcludedIds.length === 0) {
      toast.error("Please select at least one group to exclude");
      return;
    }

    // Prevent circular reference
    if (newGroupType === "exclude_group" && newGroupExcludedIds.includes(editingGroup.id)) {
      toast.error("A group cannot exclude itself");
      return;
    }

    try {
      const payload = {
        name: newGroupName,
        description: newGroupDescription,
        group_type: newGroupType,
        manual_added_assets: manualAddedAssets,
        manual_removed_assets: manualRemovedAssets,
      };

      if (newGroupType === "manual") {
        payload.assets = newGroupAssets
          .split(",")
          .map(a => a.trim().toUpperCase())
          .filter(a => a.length > 0);
        payload.excluded_group_ids = [];
      } else if (newGroupType === "exclude_group") {
        payload.excluded_group_ids = newGroupExcludedIds;
        payload.assets = [];
      } else {
        // remaining_all
        payload.assets = [];
        payload.excluded_group_ids = [];
      }

      await api.put(`/asset-groups/${editingGroup.id}`, payload);
      
      toast.success("Asset group updated successfully");
      setShowEditGroupDialog(false);
      setEditingGroup(null);
      resetGroupForm();
      fetchData();
    } catch (error) {
      console.error("Error updating asset group:", error);
      toast.error(error.response?.data?.detail || "Failed to update asset group");
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await api.delete(`/asset-groups/${id}`);
      toast.success("Asset group deleted successfully");
      setDeleteGroupId(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting asset group:", error);
      toast.error(error.response?.data?.detail || "Failed to delete asset group");
    }
  };

  // Handle full journal reset (delete all trading data)
  const handleDeleteAllData = async () => {
    if (deleteConfirmText.trim() !== 'DELETEMYDATA') {
      return;
    }
    
    setDeletingAllData(true);
    try {
      await api.delete('/user/trades/reset-all');
      
      // Close modals
      setShowDeleteAllConfirm(false);
      setShowDeleteAllWarning(false);
      setDeleteConfirmText('');
      
      // Show success toast
      toast.success("All trading data deleted. Your journal has been reset.");
      
      // Refresh all data to show empty state
      fetchData();
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error("Error deleting all data:", error);
      toast.error(error.response?.data?.detail || "Failed to delete data. Please try again.");
    } finally {
      setDeletingAllData(false);
    }
  };

  const openEditGroupDialog = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setNewGroupDescription(group.description || "");
    setNewGroupType(group.group_type || "manual");
    setNewGroupAssets(group.assets?.join(", ") || "");
    // Support both old single ID and new array format
    const excludedIds = group.excluded_group_ids || [];
    if (!excludedIds.length && group.excluded_group_id) {
      excludedIds.push(group.excluded_group_id);
    }
    setNewGroupExcludedIds(excludedIds);
    // Load manual overrides
    setManualAddedAssets(group.manual_added_assets || []);
    setManualRemovedAssets(group.manual_removed_assets || []);
    // Set current computed assets (will be recalculated in modal)
    setCurrentGroupAssets(group.computed_assets || group.assets || []);
    setNewAssetToAdd("");
    setShowEditGroupDialog(true);
  };

  // Helper to compute preview assets in edit modal
  const computePreviewAssets = () => {
    // For manual groups, start with the base assets field
    if (newGroupType === 'manual') {
      const baseAssets = newGroupAssets
        .split(",")
        .map(a => a.trim().toUpperCase())
        .filter(a => a.length > 0);
      
      // Apply overrides
      const removedSet = new Set(manualRemovedAssets.map(a => a.toUpperCase()));
      let result = baseAssets.filter(a => !removedSet.has(a.toUpperCase()));
      
      // Add manually added assets
      const existingSet = new Set(result.map(a => a.toUpperCase()));
      manualAddedAssets.forEach(a => {
        if (!existingSet.has(a.toUpperCase())) {
          result.push(a.toUpperCase());
          existingSet.add(a.toUpperCase());
        }
      });
      
      return result.sort();
    }
    
    // For dynamic groups, start with computed_assets from editingGroup
    const baseAssets = editingGroup?.computed_assets || [];
    
    // Apply overrides
    const removedSet = new Set(manualRemovedAssets.map(a => a.toUpperCase()));
    let result = baseAssets.filter(a => !removedSet.has(a.toUpperCase()));
    
    // Add manually added assets
    const existingSet = new Set(result.map(a => a.toUpperCase()));
    manualAddedAssets.forEach(a => {
      if (!existingSet.has(a.toUpperCase())) {
        result.push(a.toUpperCase());
        existingSet.add(a.toUpperCase());
      }
    });
    
    return result.sort();
  };

  // Remove asset from group (mark for manual removal)
  const handleRemoveAsset = (asset) => {
    const assetUpper = asset.toUpperCase();
    
    // If it was manually added, just remove from added list
    if (manualAddedAssets.map(a => a.toUpperCase()).includes(assetUpper)) {
      setManualAddedAssets(prev => prev.filter(a => a.toUpperCase() !== assetUpper));
    } else {
      // Otherwise, add to removed list
      if (!manualRemovedAssets.map(a => a.toUpperCase()).includes(assetUpper)) {
        setManualRemovedAssets(prev => [...prev, assetUpper]);
      }
    }
  };

  // Add asset manually
  const handleAddAssetManually = () => {
    const asset = newAssetToAdd.trim().toUpperCase();
    if (!asset) return;
    
    // Check if already in preview
    const previewAssets = computePreviewAssets();
    if (previewAssets.map(a => a.toUpperCase()).includes(asset)) {
      toast.error("Asset already in group");
      setNewAssetToAdd("");
      return;
    }
    
    // If it was manually removed, remove from removed list
    if (manualRemovedAssets.map(a => a.toUpperCase()).includes(asset)) {
      setManualRemovedAssets(prev => prev.filter(a => a.toUpperCase() !== asset));
    } else {
      // Add to manually added list
      setManualAddedAssets(prev => [...prev, asset]);
    }
    
    setNewAssetToAdd("");
  };

  // Restore a manually removed asset
  const handleRestoreAsset = (asset) => {
    const assetUpper = asset.toUpperCase();
    setManualRemovedAssets(prev => prev.filter(a => a.toUpperCase() !== assetUpper));
  };

  const toggleExcludedGroup = (groupId) => {
    setNewGroupExcludedIds(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // ==================== SETUP GROUPS HANDLERS ====================
  
  const resetSetupGroupForm = () => {
    setNewSetupGroupName("");
    setNewSetupGroupDescription("");
    setSelectedSetupIds([]);
  };

  const handleCreateSetupGroup = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot create groups in view-only mode");
      return;
    }
    
    if (!newSetupGroupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    if (selectedSetupIds.length < 2) {
      toast.error("Please select at least 2 setups");
      return;
    }
    
    try {
      await api.post('/setup-groups', {
        name: newSetupGroupName.trim(),
        description: newSetupGroupDescription.trim() || null,
        setup_ids: selectedSetupIds
      });
      
      toast.success("Setup group created successfully");
      setShowCreateSetupGroupDialog(false);
      resetSetupGroupForm();
      fetchData();
    } catch (error) {
      console.error("Error creating setup group:", error);
      toast.error(error.response?.data?.detail || "Failed to create setup group");
    }
  };

  const handleUpdateSetupGroup = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot edit groups in view-only mode");
      return;
    }
    
    if (!newSetupGroupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    if (selectedSetupIds.length < 2) {
      toast.error("Please select at least 2 setups");
      return;
    }
    
    try {
      await api.put(`/setup-groups/${editingSetupGroup.id}`, {
        name: newSetupGroupName.trim(),
        description: newSetupGroupDescription.trim() || null,
        setup_ids: selectedSetupIds
      });
      
      toast.success("Setup group updated successfully");
      setShowEditSetupGroupDialog(false);
      setEditingSetupGroup(null);
      resetSetupGroupForm();
      fetchData();
    } catch (error) {
      console.error("Error updating setup group:", error);
      toast.error(error.response?.data?.detail || "Failed to update setup group");
    }
  };

  const handleDeleteSetupGroup = async (groupId) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot delete groups in view-only mode");
      return;
    }
    
    try {
      await api.delete(`/setup-groups/${groupId}`);
      toast.success("Setup group deleted successfully");
      setDeleteSetupGroupId(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting setup group:", error);
      toast.error(error.response?.data?.detail || "Failed to delete setup group");
    }
  };

  const openEditSetupGroupDialog = (group) => {
    setEditingSetupGroup(group);
    setNewSetupGroupName(group.name);
    setNewSetupGroupDescription(group.description || "");
    setSelectedSetupIds(group.setup_ids || []);
    setShowEditSetupGroupDialog(true);
  };

  const toggleSetupSelection = (setupId) => {
    setSelectedSetupIds(prev => {
      if (prev.includes(setupId)) {
        return prev.filter(id => id !== setupId);
      } else {
        return [...prev, setupId];
      }
    });
  };

  // Setup sorting options
  const sortOptions = [
    { key: 'trades', label: 'Trades' },
    { key: 'winRate', label: 'Win Rate' },
    { key: 'avgWinRR', label: 'Avg Win RR' },
    { key: 'avgLossRR', label: 'Avg Loss RR' },
    { key: 'profitFactor', label: 'Profit Factor' }
  ];

  // Calculate metric trends based on selected comparison mode
  const metricTrends = useMemo(() => {
    // Filter to closed trades with valid outcome, sorted by close date (most recent first)
    const validTrades = allTrades
      .filter(t => t.status === 'closed' && t.outcome && ['win', 'loss', 'breakeven'].includes(t.outcome))
      .sort((a, b) => new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at));
    
    let recentTrades = [];
    let previousTrades = [];
    let insufficientData = false;
    
    const now = new Date();
    
    if (trendCompareMode === 'last_10_trades') {
      if (validTrades.length < 20) {
        insufficientData = true;
      } else {
        recentTrades = validTrades.slice(0, 10);
        previousTrades = validTrades.slice(10, 20);
      }
    } else if (trendCompareMode === 'last_30_trades') {
      if (validTrades.length < 60) {
        insufficientData = true;
      } else {
        recentTrades = validTrades.slice(0, 30);
        previousTrades = validTrades.slice(30, 60);
      }
    } else if (trendCompareMode === 'last_7_days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const fourteenDaysAgo = new Date(now);
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      recentTrades = validTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.created_at);
        return tradeDate >= sevenDaysAgo && tradeDate <= now;
      });
      
      previousTrades = validTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.created_at);
        return tradeDate >= fourteenDaysAgo && tradeDate < sevenDaysAgo;
      });
      
      if (recentTrades.length === 0 || previousTrades.length === 0) {
        insufficientData = true;
      }
    } else if (trendCompareMode === 'last_30_days') {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const sixtyDaysAgo = new Date(now);
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      
      recentTrades = validTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.created_at);
        return tradeDate >= thirtyDaysAgo && tradeDate <= now;
      });
      
      previousTrades = validTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.created_at);
        return tradeDate >= sixtyDaysAgo && tradeDate < thirtyDaysAgo;
      });
      
      if (recentTrades.length === 0 || previousTrades.length === 0) {
        insufficientData = true;
      }
    }
    
    if (insufficientData) {
      return { winRate: null, profitFactor: null, avgRR: null, insufficientData: true };
    }
    
    // Helper: calculate win rate for a group
    const calcWinRate = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const wins = trades.filter(t => t.outcome === 'win').length;
      return (wins / trades.length) * 100;
    };
    
    // Helper: calculate profit factor for a group
    const calcProfitFactor = (trades) => {
      if (!trades || trades.length === 0) return null;
      const wins = trades.filter(t => t.outcome === 'win');
      const losses = trades.filter(t => t.outcome === 'loss');
      
      const totalWinRR = wins.reduce((sum, t) => sum + (t.rr || 0), 0);
      const totalLossRR = losses.reduce((sum, t) => sum + Math.abs(t.rr || 0), 0);
      
      if (totalLossRR === 0) return totalWinRR > 0 ? Infinity : null;
      return totalWinRR / totalLossRR;
    };
    
    // Helper: calculate average RR for a group
    const calcAvgRR = (trades) => {
      if (!trades || trades.length === 0) return 0;
      const withRR = trades.filter(t => t.rr != null && !isNaN(t.rr));
      if (withRR.length === 0) return 0;
      return withRR.reduce((sum, t) => sum + t.rr, 0) / withRR.length;
    };
    
    // Helper: format trend result
    const formatTrend = (recent, previous, isPercentage = false, isProfitFactor = false) => {
      if (recent === null || previous === null) return null;
      if (previous === Infinity || recent === Infinity) return null;
      
      const delta = recent - previous;
      if (Math.abs(delta) < 0.01) return { direction: 'neutral', delta: '0' };
      
      const direction = delta > 0 ? 'up' : 'down';
      let deltaStr;
      
      if (isProfitFactor) {
        deltaStr = Math.abs(delta).toFixed(2);
      } else if (isPercentage) {
        deltaStr = `${Math.abs(delta).toFixed(0)}%`;
      } else {
        deltaStr = Math.abs(delta).toFixed(2);
      }
      
      return { direction, delta: deltaStr };
    };
    
    // Calculate metrics for both periods
    const recentWinRate = calcWinRate(recentTrades);
    const previousWinRate = calcWinRate(previousTrades);
    
    const recentPF = calcProfitFactor(recentTrades);
    const previousPF = calcProfitFactor(previousTrades);
    
    const recentAvgRR = calcAvgRR(recentTrades);
    const previousAvgRR = calcAvgRR(previousTrades);
    
    return {
      winRate: formatTrend(recentWinRate, previousWinRate, true),
      profitFactor: formatTrend(recentPF, previousPF, false, true),
      avgRR: formatTrend(recentAvgRR, previousAvgRR),
      insufficientData: false
    };
  }, [allTrades, trendCompareMode]);

  // Handle sort field click - cycles through: desc → asc → reset to default
  const handleSortClick = (field) => {
    if (setupSortField === field) {
      // Same field clicked
      if (setupSortDirection === 'desc') {
        setSetupSortDirection('asc');
      } else if (setupSortDirection === 'asc') {
        // Reset to default (trades desc)
        setSetupSortField('trades');
        setSetupSortDirection('desc');
      }
    } else {
      // New field clicked
      setSetupSortField(field);
      setSetupSortDirection('desc');
    }
  };

  // Get sorted setups with analytics (native only)
  const sortedSetupsWithAnalytics = useMemo(() => {
    // Combine setups with their analytics
    const combined = setups.map(setup => {
      const analytics = setupAnalytics.find(a => a.setup_id === setup.id) || {
        total_trades: 0,
        win_rate: 0,
        avg_win_rr: null,
        average_rr: 0,
        profit_factor: null,
        profit_factor_interpretation: "N/A"
      };
      return { setup, analytics };
    });

    // Sort based on current sort field and direction
    const sorted = [...combined].sort((a, b) => {
      let aVal, bVal;
      
      switch (setupSortField) {
        case 'trades':
          aVal = a.analytics.total_trades ?? 0;
          bVal = b.analytics.total_trades ?? 0;
          break;
        case 'winRate':
          aVal = parseFloat(a.analytics.win_rate_excluding_even ?? a.analytics.win_rate) || 0;
          bVal = parseFloat(b.analytics.win_rate_excluding_even ?? b.analytics.win_rate) || 0;
          break;
        case 'avgWinRR':
          // Parse avg win RR, treating null as -Infinity for sorting
          aVal = a.analytics.avg_win_rr !== null ? parseFloat(a.analytics.avg_win_rr) : -Infinity;
          bVal = b.analytics.avg_win_rr !== null ? parseFloat(b.analytics.avg_win_rr) : -Infinity;
          break;
        case 'avgLossRR':
          aVal = a.analytics.avg_loss_rr !== null ? parseFloat(a.analytics.avg_loss_rr) : -Infinity;
          bVal = b.analytics.avg_loss_rr !== null ? parseFloat(b.analytics.avg_loss_rr) : -Infinity;
          break;
        case 'profitFactor':
          // Parse profit factor, treating N/A or invalid as -Infinity for sorting
          aVal = a.analytics.profit_factor !== null ? (parseFloat(a.analytics.profit_factor) || 0) : -Infinity;
          bVal = b.analytics.profit_factor !== null ? (parseFloat(b.analytics.profit_factor) || 0) : -Infinity;
          break;
        default:
          aVal = a.analytics.total_trades ?? 0;
          bVal = b.analytics.total_trades ?? 0;
      }
      
      // Apply sort direction
      if (setupSortDirection === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });

    return sorted;
  }, [setups, setupAnalytics, setupSortField, setupSortDirection]);

  // Determine the best setup (only setups with at least 1 trade)
  const bestSetupId = useMemo(() => {
    const eligibleSetups = sortedSetupsWithAnalytics.filter(
      ({ analytics }) => analytics.total_trades > 0
    );
    
    if (eligibleSetups.length === 0) return null;
    
    // Sort by: Profit Factor → Win Rate → Avg RR → Trades (all descending)
    const sorted = [...eligibleSetups].sort((a, b) => {
      // 1. Highest Profit Factor
      const pfA = parseFloat(a.analytics.profit_factor) || 0;
      const pfB = parseFloat(b.analytics.profit_factor) || 0;
      if (pfB !== pfA) return pfB - pfA;
      
      // 2. Highest Win Rate (Excluding Even)
      const wrA = parseFloat(a.analytics.win_rate_excluding_even ?? a.analytics.win_rate) || 0;
      const wrB = parseFloat(b.analytics.win_rate_excluding_even ?? b.analytics.win_rate) || 0;
      if (wrB !== wrA) return wrB - wrA;
      
      // 3. Highest Avg RR
      const rrA = parseFloat(a.analytics.average_rr) || 0;
      const rrB = parseFloat(b.analytics.average_rr) || 0;
      if (rrB !== rrA) return rrB - rrA;
      
      // 4. Highest Trades
      return (b.analytics.total_trades || 0) - (a.analytics.total_trades || 0);
    });
    
    return sorted[0]?.setup.id || null;
  }, [sortedSetupsWithAnalytics]);

  // Featured setups for collapsed view (most traded, top setup, needs review)
  // ALLOWS DUPLICATES: same setup can appear in multiple categories
  const featuredSetups = useMemo(() => {
    const MIN_TRADES_FOR_RANKING = 5;
    const featured = [];

    // Debug: Log all setups
    console.log('[Featured Setups Debug] All setups:', sortedSetupsWithAnalytics.map(({ setup, analytics }) => ({
      name: setup.name,
      trades: analytics.total_trades,
      pf: analytics.profit_factor
    })));

    // 1. MOST USED - highest number of trades (no minimum required)
    const byTrades = [...sortedSetupsWithAnalytics]
      .filter(({ analytics }) => analytics.total_trades > 0)
      .sort((a, b) => (b.analytics.total_trades || 0) - (a.analytics.total_trades || 0));
    
    if (byTrades.length > 0) {
      const mostUsed = byTrades[0];
      console.log('[Featured Setups Debug] MOST USED:', mostUsed.setup.name, 'Trades:', mostUsed.analytics.total_trades);
      featured.push({ ...mostUsed, badge: 'MOST USED' });
    }

    // 2. TOP SETUP - highest Profit Factor (min 5 trades, valid PF)
    const eligibleForTop = sortedSetupsWithAnalytics.filter(
      ({ analytics }) => 
        analytics.total_trades >= MIN_TRADES_FOR_RANKING && 
        analytics.profit_factor !== null &&
        !isNaN(parseFloat(analytics.profit_factor))
    );
    
    if (eligibleForTop.length > 0) {
      const topSetup = [...eligibleForTop].sort((a, b) => {
        const pfA = parseFloat(a.analytics.profit_factor) || 0;
        const pfB = parseFloat(b.analytics.profit_factor) || 0;
        return pfB - pfA; // Descending - highest first
      })[0];
      
      console.log('[Featured Setups Debug] TOP SETUP:', topSetup.setup.name, 'PF:', topSetup.analytics.profit_factor);
      featured.push({ ...topSetup, badge: 'TOP SETUP' });
    }

    // 3. NEEDS REVIEW - lowest Profit Factor (min 5 trades, valid PF)
    const eligibleForReview = sortedSetupsWithAnalytics.filter(
      ({ analytics }) => 
        analytics.total_trades >= MIN_TRADES_FOR_RANKING && 
        analytics.profit_factor !== null &&
        !isNaN(parseFloat(analytics.profit_factor))
    );
    
    if (eligibleForReview.length > 0) {
      const needsReview = [...eligibleForReview].sort((a, b) => {
        const pfA = parseFloat(a.analytics.profit_factor) || 0;
        const pfB = parseFloat(b.analytics.profit_factor) || 0;
        return pfA - pfB; // Ascending - lowest first
      })[0];
      
      console.log('[Featured Setups Debug] NEEDS REVIEW:', needsReview.setup.name, 'PF:', needsReview.analytics.profit_factor);
      featured.push({ ...needsReview, badge: 'NEEDS REVIEW' });
    }

    console.log('[Featured Setups Debug] Final featured count:', featured.length);
    return featured;
  }, [sortedSetupsWithAnalytics]);

  // Format hold time for display (Win vs Loss comparison)
  const formatHoldTimePrecise = (ms) => {
    if (!ms || isNaN(ms) || ms <= 0) return '—';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMins = minutes % 60;
      return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else if (seconds > 0) {
      return `${seconds}s`;
    }
    return '—';
  };

  // Helper to get open timestamp from trade (check multiple field names)
  const getOpenTime = (trade) => {
    const fields = ['open_time', 'entry_time', 'created_at'];
    for (const field of fields) {
      const val = trade[field];
      if (val) {
        // Convert to milliseconds timestamp
        let ts;
        if (typeof val === 'string') {
          ts = new Date(val).getTime();
        } else if (typeof val === 'number') {
          // If timestamp < 10^12, assume it's seconds, not milliseconds
          ts = val < 1e12 ? val * 1000 : val;
        } else if (val instanceof Date) {
          ts = val.getTime();
        }
        if (ts && !isNaN(ts) && ts > 0) return ts;
      }
    }
    return null;
  };

  // Helper to get close timestamp from trade (check multiple field names)
  const getCloseTime = (trade) => {
    const fields = ['close_time', 'closed_at', 'exit_time'];
    for (const field of fields) {
      const val = trade[field];
      if (val) {
        // Convert to milliseconds timestamp
        let ts;
        if (typeof val === 'string') {
          ts = new Date(val).getTime();
        } else if (typeof val === 'number') {
          // If timestamp < 10^12, assume it's seconds, not milliseconds
          ts = val < 1e12 ? val * 1000 : val;
        } else if (val instanceof Date) {
          ts = val.getTime();
        }
        if (ts && !isNaN(ts) && ts > 0) return ts;
      }
    }
    return null;
  };

  // Calculate median of array
  const calculateMedian = (arr) => {
    if (!arr || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 
      ? sorted[mid] 
      : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  // Trade Type Hold Time data (for Win vs Loss comparison)
  const tradeTypeHoldTimes = useMemo(() => {
    const closedTrades = allTrades.filter(t => t.status === 'closed');
    
    const computeHoldTimes = (type) => {
      const typeTrades = closedTrades.filter(t => t.trade_type === type);
      const wins = typeTrades.filter(t => t.outcome === 'win');
      const losses = typeTrades.filter(t => t.outcome === 'loss');
      
      // Debug tracking
      let winsSkipped = 0;
      let lossesSkipped = 0;
      
      // Calculate hold times for wins
      const winHoldTimes = [];
      wins.forEach(trade => {
        const openTs = getOpenTime(trade);
        const closeTs = getCloseTime(trade);
        
        if (openTs && closeTs) {
          const holdMs = closeTs - openTs;
          if (holdMs > 0) {
            winHoldTimes.push(holdMs);
          }
        } else {
          winsSkipped++;
        }
      });
      
      // Calculate hold times for losses
      const lossHoldTimes = [];
      losses.forEach(trade => {
        const openTs = getOpenTime(trade);
        const closeTs = getCloseTime(trade);
        
        if (openTs && closeTs) {
          const holdMs = closeTs - openTs;
          if (holdMs > 0) {
            lossHoldTimes.push(holdMs);
          }
        } else {
          lossesSkipped++;
        }
      });
      
      // Use median for more robust calculation
      const medianHoldWinMs = calculateMedian(winHoldTimes);
      const medianHoldLossMs = calculateMedian(lossHoldTimes);
      
      // Debug log
      console.log(`[Hold Time Debug] ${type}:`, {
        totalWins: wins.length,
        winsWithTime: winHoldTimes.length,
        winsSkipped,
        totalLosses: losses.length,
        lossesWithTime: lossHoldTimes.length,
        lossesSkipped,
        medianHoldWinMs,
        medianHoldLossMs
      });
      
      return { 
        avgHoldWinMs: medianHoldWinMs, 
        avgHoldLossMs: medianHoldLossMs 
      };
    };
    
    return {
      scalp: computeHoldTimes('scalp'),
      day_trade: computeHoldTimes('day_trade'),
      swing_trade: computeHoldTimes('swing_trade')
    };
  }, [allTrades]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center" data-testid="analytics-loading">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="analytics-page">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2" data-testid="analytics-title">
              {language === 'ptBR' ? 'Analytics de Performance' : 'Performance Analytics'}
            </h2>
            <p className="text-white/60 text-base font-sans">
              {language === 'ptBR' ? 'Acompanhe sua performance de trading e eficiência dos setups' : 'Track your trading performance and setup efficiency'}
            </p>
          </div>
          
          {/* Management Actions - Hidden in public profile mode */}
          {!isSharedProfileView && (
            <div className="flex gap-2 items-center">
              {/* Link to Imported Analytics page (yearly only) */}
              <button
                onClick={handleImportClick}
                className="text-white/40 hover:text-white/70 text-sm transition-colors inline-flex items-center gap-2 px-3 py-2"
                data-testid="imported-analytics-link"
              >
                <UploadSimple size={16} weight="duotone" />
                <span className="hidden sm:inline">
                  {hasImportedData 
                    ? (language === 'ptBR' ? 'Ver dados importados' : 'View imported data') 
                    : (language === 'ptBR' ? 'Importar histórico' : 'Import historical data')}
                </span>
              </button>
              
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                data-testid="share-analytics-button"
                className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 flex items-center gap-2"
              >
                <ShareNetwork size={20} weight="duotone" />
                <span className="hidden sm:inline">{language === 'ptBR' ? 'Compartilhar' : 'Share'}</span>
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                data-testid="create-setup-button"
                className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                {language === 'ptBR' ? 'Novo Setup' : 'New Setup'}
              </Button>
              
              <Button
                onClick={() => setShowCreateGroupDialog(true)}
                data-testid="create-group-button"
                className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                {language === 'ptBR' ? 'Novo Grupo' : 'New Group'}
              </Button>
              
              <Button
                onClick={() => {
                  resetSetupGroupForm();
                  setShowCreateSetupGroupDialog(true);
                }}
                data-testid="create-setup-group-button-top"
                className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                {language === 'ptBR' ? 'Novo Grupo de Setups' : 'New Setup Group'}
              </Button>
            </div>
          )}
        </div>

        {/* Time Period Filter - PUBLIC PROFILE ONLY */}
        {isSharedProfileView && (
          <div className="mb-8 flex items-center gap-2 flex-wrap" data-testid="time-period-filter">
            <CalendarBlank size={20} weight="duotone" className="text-white/40" />
            <span className="text-white/40 text-xs font-mono mr-2">Period:</span>
            
            {/* Preset buttons */}
            {[
              { value: 'all', label: 'All Time' },
              { value: '7', label: 'Last 7 Days' },
              { value: '30', label: 'Last 30 Days' },
              { value: '90', label: 'Last 90 Days' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => {
                  setTimePeriod(option.value);
                  setShowCustomDatePicker(false);
                }}
                className={`px-3 py-1.5 text-xs font-mono rounded transition-colors ${
                  timePeriod === option.value && !showCustomDatePicker
                    ? 'bg-white/10 text-white border border-white/20'
                    : 'text-white/50 hover:text-white/70 hover:bg-white/5 border border-transparent'
                }`}
                data-testid={`period-${option.value}`}
              >
                {option.label}
              </button>
            ))}
            
            {/* Custom Range Button */}
            <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
              <PopoverTrigger asChild>
                <button
                  className={`px-3 py-1.5 text-xs font-mono rounded transition-colors flex items-center gap-1 ${
                    timePeriod === 'custom'
                      ? 'bg-white/10 text-white border border-white/20'
                      : 'text-white/50 hover:text-white/70 hover:bg-white/5 border border-transparent'
                  }`}
                  data-testid="period-custom"
                >
                  <CalendarBlank size={14} />
                  {timePeriod === 'custom' && customDateRange.start && customDateRange.end 
                    ? `${format(customDateRange.start, 'MMM d')} - ${format(customDateRange.end, 'MMM d')}`
                    : 'Custom Range'
                  }
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4 bg-[#0A0A0A] border border-white/10" align="start">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-white/40 font-mono mb-2 block">Start Date</label>
                    <Calendar
                      mode="single"
                      selected={customDateRange.start}
                      onSelect={(date) => setCustomDateRange(prev => ({ ...prev, start: date }))}
                      className="rounded border border-white/10"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 font-mono mb-2 block">End Date</label>
                    <Calendar
                      mode="single"
                      selected={customDateRange.end}
                      onSelect={(date) => setCustomDateRange(prev => ({ ...prev, end: date }))}
                      className="rounded border border-white/10"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      if (customDateRange.start && customDateRange.end) {
                        setTimePeriod('custom');
                        setShowCustomDatePicker(false);
                      } else {
                        toast.error('Please select both start and end dates');
                      }
                    }}
                    className="w-full bg-white text-black font-bold hover:bg-gray-200"
                    disabled={!customDateRange.start || !customDateRange.end}
                  >
                    Apply Range
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Direction Filter - PUBLIC PROFILE ONLY */}
        {isSharedProfileView && (
          <div className="mb-8 flex items-center gap-2 flex-wrap" data-testid="direction-filter">
            <TrendUp size={20} weight="duotone" className="text-white/40" />
            <span className="text-white/40 text-xs font-mono mr-2">Direction:</span>
            
            {[
              { value: 'all', label: 'All' },
              { value: 'long', label: 'Long' },
              { value: 'short', label: 'Short' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setDirectionFilter(option.value)}
                className={`px-3 py-1.5 text-xs font-mono rounded transition-colors ${
                  directionFilter === option.value
                    ? 'bg-white/10 text-white border border-white/20'
                    : 'text-white/50 hover:text-white/70 hover:bg-white/5 border border-transparent'
                }`}
                data-testid={`direction-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Global Metrics */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={24} weight="duotone" className="text-white" />
              <h3 className="text-2xl font-bold font-heading">{language === 'ptBR' ? 'Performance Global' : 'Global Performance'}</h3>
            </div>
            {/* Trend comparison mode selector - Hidden in public profile */}
            {!isSharedProfileView && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/40 font-mono">{language === 'ptBR' ? 'Comparar:' : 'Compare:'}</span>
              <Select value={trendCompareMode} onValueChange={setTrendCompareMode}>
                <SelectTrigger 
                  className="h-7 w-[140px] bg-[#0A0A0A] border-white/10 text-xs text-white/80"
                  data-testid="trend-compare-selector"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="last_10_trades" className="text-xs text-white/80">Last 10 trades</SelectItem>
                  <SelectItem value="last_30_trades" className="text-xs text-white/80">Last 30 trades</SelectItem>
                  <SelectItem value="last_7_days" className="text-xs text-white/80">Last 7 days</SelectItem>
                  <SelectItem value="last_30_days" className="text-xs text-white/80">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
          </div>
          
          {/* Insufficient data warning - Hidden in public profile */}
          {!isSharedProfileView && metricTrends.insufficientData && (
            <div className="mb-4 px-3 py-2 bg-white/5 border border-white/10 rounded-sm">
              <span className="text-xs text-white/50 font-mono">Not enough data for comparison</span>
            </div>
          )}
          
          {globalAnalytics && globalAnalytics.total_trades > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* TOTAL TRADES - Shows Win/Loss/Even breakdown */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-white/40 text-xs font-mono uppercase tracking-wider">Total Trades</div>
                  <ChartBar size={20} weight="duotone" className="text-white/40" />
                </div>
                <div className="text-3xl font-bold font-mono text-white">
                  {globalAnalytics.total_trades}
                </div>
                <div className="text-white/60 text-sm mt-1 flex flex-wrap gap-x-1">
                  <span>{globalAnalytics.win_count} Win</span>
                  <span className="text-white/30">·</span>
                  <span>{globalAnalytics.loss_count} Loss</span>
                  <span className="text-white/30">·</span>
                  <span>{globalAnalytics.even_count ?? (globalAnalytics.total_trades - globalAnalytics.win_count - globalAnalytics.loss_count)} Even</span>
                </div>
                {showHistoricalMonthlyAverages && globalAnalytics?.average_trades_per_month !== null && globalAnalytics?.average_trades_per_month !== undefined ? (
                  <div className="text-white/40 text-xs mt-2 font-mono" data-testid="shared-profile-trades-per-month-context">
                    {formatMonthlyAverageValue(globalAnalytics.average_trades_per_month, ` ${t('analytics.tradesPerMonth')}`, { decimals: 1 })}
                  </div>
                ) : null}
              </div>
              
              {/* WIN RATE — ALL TRADES: Wins / Total Trades */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-white/40 text-xs font-mono uppercase tracking-wider leading-tight">Win Rate — All Trades</div>
                  <Target size={20} weight="duotone" className="text-white/40 flex-shrink-0" />
                </div>
                <div className="flex items-baseline gap-2">
                  <div className={`text-3xl font-bold font-mono ${globalAnalytics.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}`}>
                    {globalAnalytics.win_rate}%
                  </div>
                  {!isSharedProfileView && metricTrends.winRate && metricTrends.winRate.direction !== 'neutral' && (
                    <div className={`flex items-center gap-1 text-xs font-mono ${
                      metricTrends.winRate.direction === 'up' ? 'text-[#00E676]' : 'text-[#FF3B30]'
                    }`}>
                      {metricTrends.winRate.direction === 'up' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                      <span>{metricTrends.winRate.delta}</span>
                    </div>
                  )}
                </div>
                <div className="text-white/40 text-xs mt-1">
                  Wins ÷ Total Trades
                </div>
              </div>
              
              {/* WIN RATE — EXCL. EVEN: Wins / (Wins + Losses) */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-white/40 text-xs font-mono uppercase tracking-wider leading-tight">Win Rate — Excl. Even</div>
                  <Target size={20} weight="duotone" className="text-white/40 flex-shrink-0" />
                </div>
                <div className={`text-3xl font-bold font-mono ${(globalAnalytics.win_rate_excluding_even ?? 0) >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}`}>
                  {globalAnalytics.win_rate_excluding_even ?? 0}%
                </div>
                <div className="text-white/40 text-xs mt-1">
                  Excludes Even trades
                </div>
              </div>
              
              <MetricCard
                title="Avg Win RR"
                value={globalAnalytics.avg_win_rr !== null ? globalAnalytics.avg_win_rr : "N/A"}
                subtitle="Winning trades only"
                icon={TrendUp}
                highlight="text-[#00E676]"
              />
              <MetricCard
                title="TOTAL R"
                value={formatAnalyticsTotalR(globalAnalytics)}
                subtitle={showHistoricalMonthlyAverages && globalAnalytics?.average_r_per_month !== null && globalAnalytics?.average_r_per_month !== undefined
                  ? formatMonthlyAverageValue(globalAnalytics.average_r_per_month, t('analytics.rPerMonth'), { signed: true, decimals: 2 })
                  : 'All trades'}
                icon={TrendUp}
                highlight={getAnalyticsTotalRColor(globalAnalytics)}
                trend={!isSharedProfileView ? metricTrends.avgRR : null}
              />
              <MetricCard
                title="Profit Factor"
                value={globalAnalytics.profit_factor !== null ? globalAnalytics.profit_factor : "N/A"}
                subtitle={getProfitFactorLabel(globalAnalytics.profit_factor)}
                icon={Trophy}
                highlight={getProfitFactorColor(globalAnalytics.profit_factor)}
                trend={!isSharedProfileView ? metricTrends.profitFactor : null}
              />
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-trades-message">
              <ChartBar size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
              {isSharedProfileView && (timePeriod !== 'all' || directionFilter !== 'all') ? (
                <>
                  <p className="text-white/60">
                    {directionFilter !== 'all' && timePeriod !== 'all'
                      ? 'No trades available for this period and direction.'
                      : directionFilter !== 'all'
                      ? `No ${directionFilter.toUpperCase()} trades available.`
                      : 'No trades available for this period.'}
                  </p>
                  <p className="text-white/40 text-sm mt-2">Try selecting a different time range or direction.</p>
                </>
              ) : (
                <>
                  <p className="text-white/60">No completed trades yet</p>
                  <p className="text-white/40 text-sm mt-2">Mark trades as win/loss to see analytics</p>
                </>
              )}
            </div>
          )}
        </div>

        {isSharedProfileView && globalAnalytics?.monthly_performance?.length ? (
          <SharedMonthlyPerformanceSection
            rows={globalAnalytics.monthly_performance}
            buildMonthUrl={buildMonthlyHistoryUrl}
          />
        ) : null}

        {/* Trade Type Comparison */}
        {breakdown && breakdown.trade_type && (
          <div className="mt-8">
            <h3 className="text-2xl font-bold font-heading mb-4">Trade Type Comparison</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["scalp", "day_trade", "swing_trade"].map((type) => {
                const data = breakdown.trade_type[type];
                const typeName = type === "scalp" ? "Scalp" : type === "day_trade" ? "Day Trade" : "Swing Trade";
                const typeColor = type === "scalp" ? "text-yellow-400" : type === "day_trade" ? "text-[#00E676]" : "text-purple-400";
                const hoverBorder = type === "scalp" ? "hover:border-yellow-500/30" : type === "day_trade" ? "hover:border-[#00E676]/30" : "hover:border-purple-500/30";
                const hasData = data && data.total_trades > 0;
                const holdTimes = tradeTypeHoldTimes[type];
                
                return (
                  <div 
                    key={type} 
                    onClick={() => hasData && navigate(getRoute('/history'), { state: { filter: { tradeType: type } } })}
                    className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-all ${
                      hasData ? `cursor-pointer ${hoverBorder} hover:bg-[#151515]` : ''
                    }`}
                    data-testid={`trade-type-card-${type}`}
                  >
                    <h4 className={`text-lg font-bold font-heading mb-4 ${typeColor}`}>{typeName}</h4>
                    {hasData ? (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                            <div className="text-2xl font-bold font-mono text-white">{data.total_trades}</div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                            <div className="text-2xl font-bold font-mono text-white">{data.win_rate_excluding_even ?? data.win_rate}%</div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">TOTAL R</div>
                            <div className={`text-2xl font-bold font-mono ${
                              getAnalyticsTotalRColor(data)
                            }`}>
                              {formatAnalyticsTotalR(data)}
                            </div>
                          </div>
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">P.FACTOR</div>
                            <div className={`text-2xl font-bold font-mono ${getProfitFactorColor(data.profit_factor)}`}>
                              {data.profit_factor}
                            </div>
                          </div>
                        </div>
                        
                        {/* Hold Time Section - HIDDEN in public profile */}
                        {!isSharedProfileView && (
                        <div className="border-t border-white/10 pt-4 mt-4">
                          <div className="text-white/30 text-xs font-mono uppercase tracking-wider mb-3">HOLD TIME</div>
                          <div className="flex gap-6">
                            <div>
                              <span className="text-white/40 text-xs">Win:</span>
                              <span className="text-[#00E676] font-mono text-sm ml-2">
                                {formatHoldTimePrecise(holdTimes?.avgHoldWinMs)}
                              </span>
                            </div>
                            <div>
                              <span className="text-white/40 text-xs">Loss:</span>
                              <span className="text-[#FF3B30] font-mono text-sm ml-2">
                                {formatHoldTimePrecise(holdTimes?.avgHoldLossMs)}
                              </span>
                            </div>
                          </div>
                        </div>
                        )}
                        
                        <div className="text-white/40 text-xs text-center pt-3 mt-3 border-t border-white/5">
                          Click to view {typeName.toLowerCase()} trades
                        </div>
                      </>
                    ) : (
                      <p className="text-white/40 text-sm">No {typeName.toLowerCase()} data</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Setup Management & Analytics - Different views for Public vs Private */}
        <div className="mt-14 mb-10">
          {isSharedProfileView ? (
            /* PUBLIC PROFILE: Adaptive logic based on active setups count */
            (() => {
              // Calculate active setups with trades
              const activeSetupsWithTrades = sortedSetupsWithAnalytics.filter(
                ({ analytics }) => analytics.total_trades > 0
              );
              const activeSetupCount = activeSetupsWithTrades.length;
              
              // Filter valid Setup Groups (exclude Ungrouped, only include groups with trades)
              const validSetupGroups = setupGroupsWithAnalytics.filter(
                group => !group.is_ungrouped && group.analytics && group.analytics.total_trades > 0
              );
              
              // CASE A: 0 setups with trades - hide section completely
              if (activeSetupCount === 0) {
                return null;
              }
              
              // CASE B: 1-3 setups with trades - show individual Setup Performance
              if (activeSetupCount <= 3) {
                return (
                  <>
                    <div className="flex items-center gap-2 mb-6">
                      <Target size={24} weight="duotone" className="text-white" />
                      <h3 className="text-2xl font-bold font-heading">Setup Performance</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {activeSetupsWithTrades.map(({ setup, analytics }) => {
                        const profitFactor = analytics.profit_factor;
                        const pfInfinite = analytics.profit_factor_infinite;
                        const pfDisplay = pfInfinite ? '∞' : (profitFactor !== null ? Number(profitFactor).toFixed(2) : 'N/A');
                        
                        const getPfColor = (pf) => {
                          if (pf === null || pf === undefined) return 'text-white/40';
                          if (pf < 1) return 'text-[#FF3B30]';
                          if (pf < 1.5) return 'text-[#FFC107]';
                          return 'text-[#00E676]';
                        };
                        
                        return (
                          <div
                            key={setup.id}
                            data-testid={`setup-public-${setup.id}`}
                            className="bg-[#121212] border border-white/10 rounded-sm p-6 transition-colors"
                          >
                            {/* Setup Header */}
                            <div className="mb-4">
                              <h4 className="text-lg font-bold font-heading mb-1" data-testid={`setup-name-public-${setup.id}`}>
                                {setup.name}
                              </h4>
                              <p className="text-white/40 text-xs font-mono">
                                {analytics.total_trades} trade{analytics.total_trades !== 1 ? 's' : ''}
                              </p>
                            </div>
                            
                            {/* Metrics Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">WIN RATE</div>
                                <div className={`text-lg font-bold font-mono ${
                                  (analytics.win_rate_excluding_even ?? analytics.win_rate ?? 0) >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'
                                }`}>
                                  {analytics.win_rate_excluding_even ?? analytics.win_rate ?? 0}%
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">TOTAL R</div>
                                <div className={`text-lg font-bold font-mono ${
                                  getAnalyticsTotalRColor(analytics)
                                }`} data-testid={`setup-total-r-public-${setup.id}`}>
                                  {formatAnalyticsTotalR(analytics)}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">P. FACTOR</div>
                                <div className={`text-lg font-bold font-mono ${pfInfinite ? 'text-[#00E676]' : getPfColor(profitFactor)}`}>
                                  {pfDisplay}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">AVG WIN RR</div>
                                <div className="text-lg font-bold font-mono text-[#00E676]">
                                  {analytics.avg_win_rr !== null ? analytics.avg_win_rr : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              }
              
              // CASE C: >3 setups with trades AND valid Setup Groups exist
              if (validSetupGroups.length > 0) {
                // Build query params for drilldown (preserve time period if set)
                const buildDrilldownParams = (groupId, setupIds) => {
                  const params = new URLSearchParams();
                  params.set('setup_group_id', groupId);
                  params.set('setup_group_setup_ids', setupIds.join(','));
                  
                  // Preserve time period filter
                  if (timePeriod === 'custom' && customDateRange.start && customDateRange.end) {
                    params.set('start_date', customDateRange.start.toISOString());
                    params.set('end_date', customDateRange.end.toISOString());
                  } else if (timePeriod !== 'all') {
                    params.set('period', timePeriod);
                  }
                  
                  // Preserve direction filter
                  if (directionFilter !== 'all') {
                    params.set('direction', directionFilter);
                  }
                  
                  return params.toString();
                };
                
                return (
                  <>
                    <div className="flex items-center gap-2 mb-6">
                      <Stack size={24} weight="duotone" className="text-white" />
                      <h3 className="text-2xl font-bold font-heading">Setup Group Performance</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {validSetupGroups.map((group) => {
                        const analytics = group.analytics || {};
                        const profitFactor = analytics.profit_factor;
                        const pfInfinite = analytics.profit_factor_infinite;
                        const pfDisplay = pfInfinite ? '∞' : (profitFactor !== null ? profitFactor.toFixed(2) : 'N/A');
                        
                        const getPfColorGroup = (pf) => {
                          if (pf === null || pf === undefined) return 'text-white/40';
                          if (pf < 1) return 'text-[#FF3B30]';
                          if (pf < 1.5) return 'text-[#FFC107]';
                          return 'text-[#00E676]';
                        };
                        
                        // Build drilldown URL for this group
                        const drilldownUrl = `${getRoute('/history')}?${buildDrilldownParams(group.group_id, group.setup_ids || [])}`;
                        // Store group name for display in History
                        const handleGroupClick = () => {
                          navigate(drilldownUrl, { 
                            state: { 
                              setupGroupName: group.group_name,
                              setupGroupId: group.group_id
                            } 
                          });
                        };
                        
                        return (
                          <button
                            key={group.group_id}
                            data-testid={`setup-group-public-${group.group_id}`}
                            className="bg-[#121212] border border-white/10 rounded-sm p-6 transition-colors hover:border-white/30 hover:bg-[#151515] cursor-pointer text-left w-full group"
                            onClick={handleGroupClick}
                          >
                            {/* Group Header */}
                            <div className="mb-4">
                              <div className="flex items-center justify-between">
                                <h4 className="text-lg font-bold font-heading mb-1" data-testid={`setup-group-name-public-${group.group_id}`}>
                                  {group.group_name}
                                </h4>
                                <span className="text-white/30 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                  View trades →
                                </span>
                              </div>
                              <p className="text-white/40 text-xs font-mono">
                                {group.setup_count} setup{group.setup_count !== 1 ? 's' : ''} · {analytics.total_trades || 0} trades
                              </p>
                            </div>
                            
                            {/* Metrics Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">WIN RATE</div>
                                <div className={`text-lg font-bold font-mono ${
                                  (analytics.win_rate_excluding_even || 0) >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'
                                }`}>
                                  {analytics.win_rate_excluding_even ?? 0}%
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">TOTAL R</div>
                                <div className={`text-lg font-bold font-mono ${
                                  getAnalyticsTotalRColor(analytics)
                                }`} data-testid={`setup-group-total-r-public-${group.group_id}`}>
                                  {formatAnalyticsTotalR(analytics)}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">P. FACTOR</div>
                                <div className={`text-lg font-bold font-mono ${pfInfinite ? 'text-[#00E676]' : getPfColorGroup(profitFactor)}`}>
                                  {pfDisplay}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">AVG WIN RR</div>
                                <div className="text-lg font-bold font-mono text-[#00E676]">
                                  {analytics.avg_win_rr !== null ? analytics.avg_win_rr : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              }
              
              // CASE D: >3 setups with trades BUT no valid Setup Groups
              return (
                <>
                  <div className="flex items-center gap-2 mb-6">
                    <Stack size={24} weight="duotone" className="text-white" />
                    <h3 className="text-2xl font-bold font-heading">Strategy Breakdown</h3>
                  </div>
                  
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center" data-testid="strategy-breakdown-unavailable">
                    <Stack size={40} weight="duotone" className="text-white/30 mx-auto mb-4" />
                    <p className="text-white/50 text-sm font-medium mb-2">Strategy breakdown unavailable</p>
                    <p className="text-white/30 text-xs max-w-md mx-auto">
                      This trader has multiple active setups but hasn&apos;t organized them into strategy groups for public analysis yet.
                    </p>
                  </div>
                </>
              );
            })()
          ) : (
            /* PRIVATE DASHBOARD: Show individual Setup Performance */
            <>
              {/* Title + Sort Controls Row */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Target size={24} weight="duotone" className="text-white" />
                  <h3 className="text-2xl font-bold font-heading">Setup Performance</h3>
                </div>
                
                {/* Sort Control */}
                {setups.length > 1 && (
                  <div className="flex items-center gap-2" data-testid="setup-sort-control">
                    <span className="text-white/40 text-xs font-mono">Sort by:</span>
                    <div className="flex gap-1">
                      {sortOptions.map(option => {
                        const isActive = setupSortField === option.key;
                        return (
                          <button
                            key={option.key}
                            onClick={() => handleSortClick(option.key)}
                            className={`px-2 py-1 text-xs font-mono rounded transition-colors flex items-center gap-1 ${
                              isActive 
                                ? 'bg-white/10 text-white' 
                                : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                            }`}
                            data-testid={`sort-${option.key}`}
                          >
                            {option.label}
                            {isActive && (
                              setupSortDirection === 'desc' 
                                ? <CaretDown size={12} weight="bold" className="text-[#00E676]" />
                                : <CaretUp size={12} weight="bold" className="text-[#00E676]" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {setups.length === 0 ? (
                <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-setups-message">
                  <Target size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
                  <p className="text-white/60 mb-4">No setups created yet</p>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    data-testid="create-first-setup-button"
                    className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
                  >
                    <Plus size={20} weight="bold" className="mr-2" />
                    Create Your First Setup
                  </Button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Collapsed View - Featured Setups */}
                  {!setupsExpanded && featuredSetups.length > 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {featuredSetups.map(({ setup, analytics, badge }) => {
                      const isBestSetup = setup.id === bestSetupId;
                      const badgeStyles = {
                        'MOST USED': 'bg-blue-500/10 border-blue-500/30 text-blue-400',
                        'TOP SETUP': 'bg-[#00E676]/10 border-[#00E676]/30 text-[#00E676]',
                        'NEEDS REVIEW': 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                      };
                      const hasTradesForDrilldown = analytics.total_trades > 0;
                      
                      // In public mode, navigate to history filtered by setup (setup detail pages don't exist in public namespace)
                      const handleSetupClick = () => {
                        if (!hasTradesForDrilldown) return;
                        if (isSharedProfileView) {
                          navigate(getRoute('/history'), { state: { filter: { setup: setup.name } } });
                        } else {
                          navigate(`/analytics/setup/${setup.id}`);
                        }
                      };
                      
                      return (
                        <div
                          key={setup.id}
                          data-testid={`featured-setup-${setup.id}`}
                          onClick={handleSetupClick}
                          className={`bg-[#121212] rounded-sm p-6 transition-all border ${
                            badge === 'TOP SETUP' 
                              ? 'border-[#00E676]/30 shadow-[0_0_20px_rgba(0,230,118,0.08)]'
                              : badge === 'NEEDS REVIEW'
                              ? 'border-orange-500/20'
                              : 'border-white/10'
                          } ${
                            hasTradesForDrilldown 
                              ? 'cursor-pointer hover:border-white/30 hover:bg-[#151515]' 
                              : ''
                          }`}
                        >
                          {/* Badge */}
                          <div className="mb-4">
                            <span className={`text-[10px] px-2 py-1 rounded border font-mono uppercase tracking-wider ${badgeStyles[badge]}`}>
                              {badge}
                            </span>
                          </div>
                          
                          {/* Setup Name */}
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="text-lg font-bold font-heading truncate flex-1 pr-2">
                              {setup.name}
                            </h4>
                            {/* Hide edit button in public mode */}
                            {!isSharedProfileView && (
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                onClick={() => openEditDialog(setup)}
                                variant="ghost"
                                size="sm"
                                className="text-white/40 hover:text-white hover:bg-white/5 h-7 w-7 p-0"
                              >
                                <Pencil size={14} weight="duotone" />
                              </Button>
                            </div>
                            )}
                          </div>
                          
                          {/* Key Metrics */}
                          {analytics.total_trades > 0 && (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-2">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">TRADES</div>
                                <div className="text-lg font-bold font-mono text-white">
                                  {analytics.total_trades}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-2">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">WIN RATE</div>
                                <div className="text-lg font-bold font-mono text-white">
                                  {analytics.win_rate_excluding_even ?? analytics.win_rate}%
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-2">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">AVG LOSS RR</div>
                                <div className="text-lg font-bold font-mono text-white">
                                  {analytics.avg_loss_rr !== null ? `${analytics.avg_loss_rr}R` : '—'}
                                </div>
                              </div>
                              <div className="bg-[#000000] border border-white/10 rounded-sm p-2">
                                <div className="text-white/40 text-[10px] font-mono mb-0.5">PROFIT FACTOR</div>
                                <div className={`text-lg font-bold font-mono ${getProfitFactorColor(analytics.profit_factor)}`}>
                                  {analytics.profit_factor !== null ? analytics.profit_factor : 'N/A'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Show All Button */}
                  {setups.length > featuredSetups.length && (
                    <button
                      onClick={() => setSetupsExpanded(true)}
                      className="w-full mt-6 py-3 text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 border border-white/10 rounded-sm transition-colors flex items-center justify-center gap-2"
                      data-testid="show-all-setups-button"
                    >
                      <CaretDown size={16} weight="bold" />
                      Show all {setups.length} setups
                    </button>
                  )}
                </>
              )}
              
              {/* Expanded View - Full Setup List */}
              {(setupsExpanded || featuredSetups.length === 0) && (
                <>
                  {sortedSetupsWithAnalytics.map(({ setup, analytics }) => {
                    const isBestSetup = setup.id === bestSetupId;
                    const featuredInfo = featuredSetups.find(f => f.setup.id === setup.id);
                    const hasTradesForDrilldown = analytics.total_trades > 0;
                    
                    // In public mode, navigate to history filtered by setup
                    const handleSetupClick = () => {
                      if (!hasTradesForDrilldown) return;
                      if (isSharedProfileView) {
                        navigate(getRoute('/history'), { state: { filter: { setup: setup.name } } });
                      } else {
                        navigate(`/analytics/setup/${setup.id}`);
                      }
                    };
                    
                    return (
                      <div
                        key={setup.id}
                        data-testid={`setup-${setup.id}`}
                        onClick={handleSetupClick}
                        className={`bg-[#121212] rounded-sm p-6 transition-all ${
                          isBestSetup 
                            ? 'border border-[#00E676]/30 shadow-[0_0_20px_rgba(0,230,118,0.08)]' 
                            : 'border border-white/10'
                        } ${
                          hasTradesForDrilldown 
                            ? 'cursor-pointer hover:border-white/30 hover:bg-[#151515]' 
                            : ''
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 
                                className="text-xl font-bold font-heading"
                                data-testid={`setup-name-${setup.id}`}
                              >
                                {setup.name}
                              </h4>
                              {featuredInfo && (
                                <span className={`text-[10px] px-2 py-0.5 rounded border font-mono uppercase tracking-wider ${
                                  featuredInfo.badge === 'MOST USED' 
                                    ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
                                    : featuredInfo.badge === 'TOP SETUP'
                                    ? 'bg-[#00E676]/10 border-[#00E676]/30 text-[#00E676]'
                                    : 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                                }`}>
                                  {featuredInfo.badge}
                                </span>
                              )}
                              {analytics.total_trades === 0 && (
                                <span className="text-xs px-2 py-1 bg-white/5 border border-white/10 rounded text-white/40">
                                  No trades
                                </span>
                              )}
                            </div>
                            {setup.description && (
                              <p className="text-white/60 text-sm">{setup.description}</p>
                            )}
                          </div>
                          
                          {/* Hide edit/delete buttons in public mode */}
                          {!isSharedProfileView && (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              onClick={() => openEditDialog(setup)}
                              data-testid={`edit-setup-${setup.id}`}
                              variant="ghost"
                              size="sm"
                              className="text-white/60 hover:text-white hover:bg-white/5"
                            >
                              <Pencil size={16} weight="duotone" />
                            </Button>
                            <Button
                              onClick={() => setDeleteId(setup.id)}
                              data-testid={`delete-setup-${setup.id}`}
                              variant="ghost"
                              size="sm"
                              className="text-white/40 hover:text-[#FF3B30] hover:bg-transparent"
                            >
                              <Trash size={16} weight="duotone" />
                            </Button>
                          </div>
                          )}
                        </div>

                        {analytics.total_trades > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                              <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                              <div className="text-xl font-bold font-mono text-white" data-testid={`setup-trades-${setup.id}`}>
                                {analytics.total_trades}
                              </div>
                            </div>
                            <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                              <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                              <div className="text-xl font-bold font-mono text-white" data-testid={`setup-winrate-${setup.id}`}>
                                {analytics.win_rate_excluding_even ?? analytics.win_rate}%
                              </div>
                            </div>
                            <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                              <div className="text-white/40 text-xs font-mono mb-1">AVG WIN RR</div>
                              <div className="text-xl font-bold font-mono text-[#00E676]" data-testid={`setup-avg-win-rr-${setup.id}`}>
                                {analytics.avg_win_rr !== null ? analytics.avg_win_rr : 'N/A'}
                              </div>
                            </div>
                            <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                              <div className="text-white/40 text-xs font-mono mb-1">AVG LOSS RR</div>
                              <div className="text-xl font-bold font-mono text-white" data-testid={`setup-loss-rr-${setup.id}`}>
                                {analytics.avg_loss_rr !== null ? `${analytics.avg_loss_rr}R` : '—'}
                              </div>
                            </div>
                            <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                              <div className="text-white/40 text-xs font-mono mb-1">PROFIT FACTOR</div>
                              <div className="flex items-baseline gap-2">
                                <div
                                  className={`text-xl font-bold font-mono ${getProfitFactorColor(analytics.profit_factor)}`}
                                  data-testid={`setup-pf-${setup.id}`}
                                >
                                  {analytics.profit_factor !== null ? analytics.profit_factor : 'N/A'}
                                </div>
                                <span className="text-xs text-white/40">{getProfitFactorLabel(analytics.profit_factor)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Show Less Button (only when expanded and there are featured setups) */}
                  {setupsExpanded && featuredSetups.length > 0 && setups.length > featuredSetups.length && (
                    <button
                      onClick={() => setSetupsExpanded(false)}
                      className="w-full mt-6 py-3 text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 border border-white/10 rounded-sm transition-colors flex items-center justify-center gap-2"
                      data-testid="show-less-setups-button"
                    >
                      <CaretUp size={16} weight="bold" />
                      Show less
                    </button>
                  )}
                </>
              )}
            </div>
          )}
            </>
          )}
        </div>

        {/* Asset Groups Management - HIDDEN in public profile mode */}
        {!isSharedProfileView && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Target size={24} weight="duotone" className="text-white" />
              <h3 className="text-2xl font-bold font-heading">Asset Groups</h3>
            </div>
            {assetGroups.length > 0 && (
              <Button
                onClick={() => {
                  resetGroupForm();
                  setShowCreateGroupDialog(true);
                }}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="add-asset-group-btn"
              >
                <Plus size={16} weight="bold" className="mr-2" />
                New Asset Group
              </Button>
            )}
          </div>

          {assetGroups.length === 0 ? (
            <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-groups-message">
              <Target size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
              <p className="text-white/60 mb-4">No asset groups created yet</p>
              <Button
                onClick={() => setShowCreateGroupDialog(true)}
                data-testid="create-first-group-button"
                className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Create Your First Group
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assetGroups.map((group) => (
                <div
                  key={group.id}
                  data-testid={`group-${group.id}`}
                  className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-colors ${
                    !isSharedProfileView ? 'hover:border-white/20 cursor-pointer' : ''
                  }`}
                  onClick={() => !isSharedProfileView && navigate(`/analytics/asset-groups/${group.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-bold font-heading" data-testid={`group-name-${group.id}`}>
                          {group.name}
                        </h4>
                        {group.group_type && group.group_type !== "manual" && (
                          <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                            group.group_type === "remaining_all" 
                              ? "bg-[#00E676]/10 text-[#00E676]" 
                              : "bg-[#FFC107]/10 text-[#FFC107]"
                          }`}>
                            {group.group_type === "remaining_all" ? "AUTO" : "EXCLUDE"}
                          </span>
                        )}
                      </div>
                      {group.description && (
                        <p className="text-white/60 text-sm mb-3">{group.description}</p>
                      )}
                    </div>
                    
                    {/* Hide edit/delete buttons in public mode */}
                    {!isSharedProfileView && (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        onClick={() => openEditGroupDialog(group)}
                        data-testid={`edit-group-${group.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-white/60 hover:text-white hover:bg-white/5"
                      >
                        <Pencil size={16} weight="duotone" />
                      </Button>
                      <Button
                        onClick={() => setDeleteGroupId(group.id)}
                        data-testid={`delete-group-${group.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-white/40 hover:text-[#FF3B30] hover:bg-transparent"
                      >
                        <Trash size={16} weight="duotone" />
                      </Button>
                    </div>
                    )}
                  </div>

                  <div>
                    {group.group_type === "manual" || !group.group_type ? (
                      <>
                        {/* Use normalized_assets if available, otherwise fallback to assets */}
                        {(() => {
                          const displayAssets = group.normalized_assets || group.assets || [];
                          const tradeCounts = group.asset_trade_counts || {};
                          const isExpanded = expandedAssetGroups[group.id];
                          const totalAssets = displayAssets.length;
                          const previewAssets = isExpanded ? displayAssets : displayAssets.slice(0, ASSETS_PREVIEW_LIMIT);
                          const remainingCount = totalAssets - ASSETS_PREVIEW_LIMIT;
                          
                          return (
                            <>
                              <div className="text-white/40 text-xs font-mono mb-2">
                                ASSETS ({totalAssets})
                              </div>
                              <div className="flex flex-wrap gap-2 items-center">
                                {previewAssets.map((asset) => (
                                  <span
                                    key={asset}
                                    className="px-2 py-1 bg-[#000000] border border-white/10 rounded text-xs font-mono text-white"
                                    title={tradeCounts[asset] ? `${tradeCounts[asset]} trades` : undefined}
                                  >
                                    {asset}
                                  </span>
                                ))}
                                {!isExpanded && remainingCount > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAssetGroups(prev => ({ ...prev, [group.id]: true }));
                                    }}
                                    className="px-2 py-1 text-xs font-mono text-[#00E676] hover:text-[#00E676]/80 transition-colors"
                                    data-testid={`view-all-assets-${group.id}`}
                                  >
                                    +{remainingCount} more
                                  </button>
                                )}
                                {isExpanded && totalAssets > ASSETS_PREVIEW_LIMIT && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAssetGroups(prev => ({ ...prev, [group.id]: false }));
                                    }}
                                    className="px-2 py-1 text-xs font-mono text-white/40 hover:text-white/60 transition-colors"
                                    data-testid={`show-less-assets-${group.id}`}
                                  >
                                    Show less
                                  </button>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        <div className="text-white/40 text-xs font-mono mb-2">
                          {group.group_type === "remaining_all" 
                            ? "INCLUDES ALL REMAINING ASSETS" 
                            : (() => {
                                // Support both old single ID and new array format
                                const excludedIds = group.excluded_group_ids || [];
                                if (!excludedIds.length && group.excluded_group_id) {
                                  excludedIds.push(group.excluded_group_id);
                                }
                                const excludedNames = excludedIds
                                  .map(id => assetGroups.find(g => g.id === id)?.name)
                                  .filter(Boolean);
                                if (excludedNames.length === 0) return "EXCLUDES: Unknown";
                                if (excludedNames.length <= 2) {
                                  return `EXCLUDES: ${excludedNames.join(', ')}`;
                                }
                                return `EXCLUDES: ${excludedNames.length} groups`;
                              })()
                          }
                        </div>
                        {/* Use normalized_assets if available for dynamic groups */}
                        {(() => {
                          const displayAssets = group.normalized_assets || group.computed_assets || [];
                          const tradeCounts = group.asset_trade_counts || {};
                          const isExpanded = expandedAssetGroups[group.id];
                          const totalAssets = displayAssets.length;
                          const previewAssets = isExpanded ? displayAssets : displayAssets.slice(0, ASSETS_PREVIEW_LIMIT);
                          const remainingCount = totalAssets - ASSETS_PREVIEW_LIMIT;
                          
                          if (totalAssets === 0) {
                            return <p className="text-white/40 text-sm italic">No assets yet - trade more to populate</p>;
                          }
                          
                          return (
                            <div className="flex flex-wrap gap-2 items-center">
                              {previewAssets.map((asset) => (
                                <span
                                  key={asset}
                                  className="px-2 py-1 bg-[#000000] border border-white/10 rounded text-xs font-mono text-white/60"
                                  title={tradeCounts[asset] ? `${tradeCounts[asset]} trades` : undefined}
                                >
                                  {asset}
                                </span>
                              ))}
                              {!isExpanded && remainingCount > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAssetGroups(prev => ({ ...prev, [group.id]: true }));
                                  }}
                                  className="px-2 py-1 text-xs font-mono text-[#00E676] hover:text-[#00E676]/80 transition-colors"
                                  data-testid={`view-all-assets-${group.id}`}
                                >
                                  +{remainingCount} more
                                </button>
                              )}
                              {isExpanded && totalAssets > ASSETS_PREVIEW_LIMIT && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAssetGroups(prev => ({ ...prev, [group.id]: false }));
                                  }}
                                  className="px-2 py-1 text-xs font-mono text-white/40 hover:text-white/60 transition-colors"
                                  data-testid={`show-less-assets-${group.id}`}
                                >
                                  Show less
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
        
        {/* Setup Groups Management - HIDDEN in public profile mode */}
        {!isSharedProfileView && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Stack size={24} weight="duotone" className="text-white" />
              <h3 className="text-2xl font-bold font-heading">Setup Groups</h3>
            </div>
            {setupGroups.length > 0 && (
              <Button
                onClick={() => {
                  resetSetupGroupForm();
                  setShowCreateSetupGroupDialog(true);
                }}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="add-setup-group-btn"
              >
                <Plus size={16} weight="bold" className="mr-2" />
                New Setup Group
              </Button>
            )}
          </div>

          {setupGroups.length === 0 ? (
            <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-setup-groups-message">
              <Stack size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
              <p className="text-white/60 mb-2">No setup groups created yet</p>
              <p className="text-white/40 text-sm mb-4">
                Group related setups together to compare variations of the same strategy
              </p>
              <Button
                onClick={() => {
                  resetSetupGroupForm();
                  setShowCreateSetupGroupDialog(true);
                }}
                data-testid="create-first-setup-group-btn"
                className="bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Create Your First Setup Group
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {setupGroups.map((group) => (
                <div
                  key={group.id}
                  data-testid={`setup-group-${group.id}`}
                  className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-colors ${
                    !isSharedProfileView ? 'hover:border-white/20 cursor-pointer' : ''
                  }`}
                  onClick={() => !isSharedProfileView && navigate(`/analytics/setup-groups/${group.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold font-heading mb-1" data-testid={`setup-group-name-${group.id}`}>
                        {group.name}
                      </h4>
                      <p className="text-white/40 text-xs font-mono">
                        {group.setup_ids?.length || 0} setups
                      </p>
                      {group.description && (
                        <p className="text-white/60 text-sm mt-2">{group.description}</p>
                      )}
                    </div>
                    
                    {/* Hide edit/delete buttons in public mode */}
                    {!isSharedProfileView && (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        onClick={() => openEditSetupGroupDialog(group)}
                        data-testid={`edit-setup-group-${group.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-white/60 hover:text-white hover:bg-white/5"
                      >
                        <Pencil size={16} weight="duotone" />
                      </Button>
                      <Button
                        onClick={() => setDeleteSetupGroupId(group.id)}
                        data-testid={`delete-setup-group-${group.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-white/40 hover:text-[#FF3B30] hover:bg-transparent"
                      >
                        <Trash size={16} weight="duotone" />
                      </Button>
                    </div>
                    )}
                  </div>

                  <div>
                    <div className="text-white/40 text-xs font-mono mb-2">SETUPS</div>
                    <div className="flex flex-wrap gap-2">
                      {(group.setup_names || []).map((name, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-[#000000] border border-white/10 rounded text-xs font-mono text-white"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      
      {/* Danger Zone - Delete All Data - HIDDEN in public mode */}
      {!isSharedProfileView && (
      <div className="w-full max-w-[1400px] mx-auto mt-16 mb-8 pt-8 border-t border-white/5">
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowDeleteAllWarning(true)}
            className="text-xs text-gray-500 hover:text-red-400 transition-colors inline-flex items-center gap-1.5"
            data-testid="delete-all-data-btn"
          >
            <Trash size={12} weight="bold" />
            Delete all data
          </button>
        </div>
      </div>
      )}

      {/* Create Setup Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#121212] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Create New Setup</DialogTitle>
            <DialogDescription className="text-white/60">
              Add a new trading setup to track its performance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="setup-name" className="text-white/80 mb-2 block">Setup Name</Label>
              <Input
                id="setup-name"
                data-testid="setup-name-input"
                placeholder="e.g., Breakout, Support/Resistance"
                value={newSetupName}
                onChange={(e) => setNewSetupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="setup-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="setup-description"
                data-testid="setup-description-input"
                placeholder="Brief description of this setup"
                value={newSetupDescription}
                onChange={(e) => setNewSetupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSetup}
              data-testid="confirm-create-setup"
              className="bg-white text-black font-bold hover:bg-gray-200"
            >
              Create Setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Setup Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#121212] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Edit Setup</DialogTitle>
            <DialogDescription className="text-white/60">
              Update setup details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-setup-name" className="text-white/80 mb-2 block">Setup Name</Label>
              <Input
                id="edit-setup-name"
                data-testid="edit-setup-name-input"
                value={newSetupName}
                onChange={(e) => setNewSetupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="edit-setup-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="edit-setup-description"
                data-testid="edit-setup-description-input"
                value={newSetupDescription}
                onChange={(e) => setNewSetupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSetup}
              data-testid="confirm-edit-setup"
              className="bg-white text-black font-bold hover:bg-gray-200"
            >
              Update Setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Delete Setup</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Are you sure you want to delete this setup? Trades associated with this setup will not be deleted, but will lose their setup assignment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-setup"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-setup"
              onClick={() => handleDeleteSetup(deleteId)}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Asset Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={(open) => {
        setShowCreateGroupDialog(open);
        if (!open) resetGroupForm();
      }}>
        <DialogContent className="bg-[#121212] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Create New Asset Group</DialogTitle>
            <DialogDescription className="text-white/60">
              Create a group of assets to track performance together
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="group-name" className="text-white/80 mb-2 block">Group Name</Label>
              <Input
                id="group-name"
                data-testid="group-name-input"
                placeholder="e.g., Big Caps, Small Caps, DeFi"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="group-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="group-description"
                data-testid="group-description-input"
                placeholder="Brief description of this group"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Group Type</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="group-type"
                    value="manual"
                    checked={newGroupType === "manual"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Manual - Select specific assets</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="group-type"
                    value="remaining_all"
                    checked={newGroupType === "remaining_all"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Remaining - All assets not in other manual groups</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="group-type"
                    value="exclude_group"
                    checked={newGroupType === "exclude_group"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Exclude - All assets except those in another group</span>
                </label>
              </div>
            </div>

            {newGroupType === "manual" && (
              <div>
                <Label htmlFor="group-assets" className="text-white/80 mb-2 block">Assets (comma-separated)</Label>
                <Input
                  id="group-assets"
                  data-testid="group-assets-input"
                  placeholder="BTC, ETH, SOL, AVAX"
                  value={newGroupAssets}
                  onChange={(e) => setNewGroupAssets(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                />
                <p className="text-xs text-white/40 mt-2">
                  Enter asset symbols separated by commas. They will be automatically converted to uppercase.
                </p>
              </div>
            )}

            {newGroupType === "exclude_group" && (
              <div>
                <Label className="text-white/80 mb-2 block">Exclude Assets From</Label>
                <div className="max-h-48 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-2">
                  {assetGroups
                    .filter(g => g.group_type === "manual" || !g.group_type)
                    .length === 0 ? (
                    <p className="text-white/40 text-sm">No manual groups available. Create a manual group first.</p>
                  ) : (
                    assetGroups
                      .filter(g => g.group_type === "manual" || !g.group_type)
                      .map(g => (
                        <label
                          key={g.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newGroupExcludedIds.includes(g.id)}
                            onChange={() => toggleExcludedGroup(g.id)}
                            className="w-4 h-4 accent-[#00E676]"
                          />
                          <span className="text-white text-sm">{g.name}</span>
                          <span className="text-white/40 text-xs ml-auto">
                            {g.assets?.length || 0} assets
                          </span>
                        </label>
                      ))
                  )}
                </div>
                {newGroupExcludedIds.length > 0 && (
                  <p className="text-white/60 text-xs mt-2">
                    {newGroupExcludedIds.length} group{newGroupExcludedIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                <p className="text-xs text-white/40 mt-2">
                  Select one or more groups to exclude assets from. This group will include all known assets except those in the selected groups.
                </p>
              </div>
            )}

            {newGroupType === "remaining_all" && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-white/60 text-sm">
                  This group will automatically include all assets from your trades that are not assigned to any manual group.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateGroupDialog(false);
                resetGroupForm();
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              data-testid="confirm-create-group"
              className="bg-white text-black font-bold hover:bg-gray-200"
            >
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Group Dialog */}
      <Dialog open={showEditGroupDialog} onOpenChange={(open) => {
        setShowEditGroupDialog(open);
        if (!open) {
          setEditingGroup(null);
          resetGroupForm();
        }
      }}>
        <DialogContent className="bg-[#121212] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Edit Asset Group</DialogTitle>
            <DialogDescription className="text-white/60">
              Update group details and assets
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-group-name" className="text-white/80 mb-2 block">Group Name</Label>
              <Input
                id="edit-group-name"
                data-testid="edit-group-name-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="edit-group-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="edit-group-description"
                data-testid="edit-group-description-input"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Group Type</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-group-type"
                    value="manual"
                    checked={newGroupType === "manual"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Manual - Select specific assets</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-group-type"
                    value="remaining_all"
                    checked={newGroupType === "remaining_all"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Remaining - All assets not in other manual groups</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-group-type"
                    value="exclude_group"
                    checked={newGroupType === "exclude_group"}
                    onChange={(e) => setNewGroupType(e.target.value)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white text-sm">Exclude - All assets except those in another group</span>
                </label>
              </div>
            </div>

            {newGroupType === "manual" && (
              <div>
                <Label htmlFor="edit-group-assets" className="text-white/80 mb-2 block">Assets (comma-separated)</Label>
                <Input
                  id="edit-group-assets"
                  data-testid="edit-group-assets-input"
                  value={newGroupAssets}
                  onChange={(e) => setNewGroupAssets(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                />
                <p className="text-xs text-white/40 mt-2">
                  Enter asset symbols separated by commas.
                </p>
              </div>
            )}

            {newGroupType === "exclude_group" && (
              <div>
                <Label className="text-white/80 mb-2 block">Exclude Assets From</Label>
                <div className="max-h-48 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-2">
                  {assetGroups
                    .filter(g => (g.group_type === "manual" || !g.group_type) && g.id !== editingGroup?.id)
                    .length === 0 ? (
                    <p className="text-white/40 text-sm">No other manual groups available.</p>
                  ) : (
                    assetGroups
                      .filter(g => (g.group_type === "manual" || !g.group_type) && g.id !== editingGroup?.id)
                      .map(g => (
                        <label
                          key={g.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newGroupExcludedIds.includes(g.id)}
                            onChange={() => toggleExcludedGroup(g.id)}
                            className="w-4 h-4 accent-[#00E676]"
                          />
                          <span className="text-white text-sm">{g.name}</span>
                          <span className="text-white/40 text-xs ml-auto">
                            {g.assets?.length || 0} assets
                          </span>
                        </label>
                      ))
                  )}
                </div>
                {newGroupExcludedIds.length > 0 && (
                  <p className="text-white/60 text-xs mt-2">
                    {newGroupExcludedIds.length} group{newGroupExcludedIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}

            {newGroupType === "remaining_all" && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-white/60 text-sm">
                  This group will automatically include all assets from your trades that are not assigned to any manual group.
                </p>
              </div>
            )}

            {/* Current Assets Preview with Manual Override Controls */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <Label className="text-white/80 mb-2 block">Current Assets in Group</Label>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 min-h-[60px] max-h-[150px] overflow-y-auto">
                {computePreviewAssets().length === 0 ? (
                  <p className="text-white/40 text-sm italic">No assets in this group yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {computePreviewAssets().map((asset) => (
                      <span
                        key={asset}
                        className="px-2 py-1 bg-[#1a1a1a] border border-white/10 rounded text-xs font-mono text-white flex items-center gap-2 group"
                      >
                        {asset}
                        <button
                          type="button"
                          onClick={() => handleRemoveAsset(asset)}
                          className="text-white/30 hover:text-[#FF3B30] transition-colors"
                          title="Remove from group"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-white/40 mt-2">
                Click the × on any asset to remove it from this group.
              </p>
            </div>

            {/* Add Assets Manually */}
            <div>
              <Label className="text-white/80 mb-2 block">Add Assets Manually</Label>
              <div className="flex gap-2">
                <Input
                  value={newAssetToAdd}
                  onChange={(e) => setNewAssetToAdd(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAssetManually();
                    }
                  }}
                  placeholder="e.g. ALOW, SHER"
                  className="bg-[#0A0A0A] border-white/10 text-white font-mono flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddAssetManually}
                  disabled={!newAssetToAdd.trim()}
                  className="bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
                >
                  <Plus size={16} weight="bold" />
                </Button>
              </div>
              <p className="text-xs text-white/40 mt-2">
                Add assets that should be in this group even if the base rule doesn&apos;t include them.
              </p>
            </div>

            {/* Manual Overrides Summary */}
            {(manualAddedAssets.length > 0 || manualRemovedAssets.length > 0) && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3 space-y-2">
                <p className="text-white/60 text-xs font-mono uppercase tracking-wider mb-2">Manual Overrides</p>
                {manualAddedAssets.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-[#00E676] text-xs">+ Added:</span>
                    <div className="flex flex-wrap gap-1">
                      {manualAddedAssets.map(asset => (
                        <span key={asset} className="text-xs font-mono text-white/80">{asset}</span>
                      ))}
                    </div>
                  </div>
                )}
                {manualRemovedAssets.length > 0 && (
                  <div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#FF3B30] text-xs">− Removed:</span>
                      <div className="flex flex-wrap gap-1">
                        {manualRemovedAssets.map(asset => (
                          <span 
                            key={asset} 
                            className="text-xs font-mono text-white/50 cursor-pointer hover:text-white/80 hover:underline"
                            onClick={() => handleRestoreAsset(asset)}
                            title="Click to restore"
                          >
                            {asset}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-white/30 mt-1">Click a removed asset to restore it.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditGroupDialog(false);
                setEditingGroup(null);
                resetGroupForm();
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateGroup}
              data-testid="confirm-edit-group"
              className="bg-white text-black font-bold hover:bg-gray-200"
            >
              Update Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Asset Group Confirmation */}
      <AlertDialog open={!!deleteGroupId} onOpenChange={() => setDeleteGroupId(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Delete Asset Group</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Are you sure you want to delete this asset group? Trades linked to this group will not be deleted, but will lose their group assignment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-group"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-group"
              onClick={() => handleDeleteGroup(deleteGroupId)}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Setup Group Dialog */}
      <Dialog open={showCreateSetupGroupDialog} onOpenChange={(open) => {
        setShowCreateSetupGroupDialog(open);
        if (!open) resetSetupGroupForm();
      }}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Create Setup Group</DialogTitle>
            <DialogDescription className="text-white/60">
              Group related setups together to compare variations of the same strategy
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="setup-group-name" className="text-white/80 mb-2 block">Group Name</Label>
              <Input
                id="setup-group-name"
                data-testid="setup-group-name-input"
                placeholder="e.g., Elliot Setups, Breakout Variations"
                value={newSetupGroupName}
                onChange={(e) => setNewSetupGroupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="setup-group-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="setup-group-description"
                data-testid="setup-group-description-input"
                placeholder="Brief description of this group"
                value={newSetupGroupDescription}
                onChange={(e) => setNewSetupGroupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Select Setups (minimum 2)</Label>
              <div className="max-h-48 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-2">
                {setups.filter(s => !s.is_default).length === 0 ? (
                  <p className="text-white/40 text-sm">No custom setups available. Create setups first.</p>
                ) : (
                  setups.filter(s => !s.is_default).map((setup) => (
                    <label
                      key={setup.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSetupIds.includes(setup.id)}
                        onChange={() => toggleSetupSelection(setup.id)}
                        className="w-4 h-4 accent-[#00E676]"
                      />
                      <span className="text-white text-sm">{setup.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedSetupIds.length > 0 && (
                <p className="text-white/40 text-xs mt-2">
                  {selectedSetupIds.length} setup{selectedSetupIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateSetupGroupDialog(false);
                resetSetupGroupForm();
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSetupGroup}
              disabled={selectedSetupIds.length < 2 || !newSetupGroupName.trim()}
              data-testid="confirm-create-setup-group"
              className="bg-white text-black font-bold hover:bg-gray-200 disabled:opacity-50"
            >
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Setup Group Dialog */}
      <Dialog open={showEditSetupGroupDialog} onOpenChange={(open) => {
        setShowEditSetupGroupDialog(open);
        if (!open) {
          setEditingSetupGroup(null);
          resetSetupGroupForm();
        }
      }}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Edit Setup Group</DialogTitle>
            <DialogDescription className="text-white/60">
              Update group details and included setups
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-setup-group-name" className="text-white/80 mb-2 block">Group Name</Label>
              <Input
                id="edit-setup-group-name"
                data-testid="edit-setup-group-name-input"
                value={newSetupGroupName}
                onChange={(e) => setNewSetupGroupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="edit-setup-group-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="edit-setup-group-description"
                data-testid="edit-setup-group-description-input"
                value={newSetupGroupDescription}
                onChange={(e) => setNewSetupGroupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Select Setups (minimum 2)</Label>
              <div className="max-h-48 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-2">
                {setups.filter(s => !s.is_default).length === 0 ? (
                  <p className="text-white/40 text-sm">No custom setups available.</p>
                ) : (
                  setups.filter(s => !s.is_default).map((setup) => (
                    <label
                      key={setup.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSetupIds.includes(setup.id)}
                        onChange={() => toggleSetupSelection(setup.id)}
                        className="w-4 h-4 accent-[#00E676]"
                      />
                      <span className="text-white text-sm">{setup.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedSetupIds.length > 0 && (
                <p className="text-white/40 text-xs mt-2">
                  {selectedSetupIds.length} setup{selectedSetupIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditSetupGroupDialog(false);
                setEditingSetupGroup(null);
                resetSetupGroupForm();
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSetupGroup}
              disabled={selectedSetupIds.length < 2 || !newSetupGroupName.trim()}
              data-testid="confirm-edit-setup-group"
              className="bg-white text-black font-bold hover:bg-gray-200 disabled:opacity-50"
            >
              Update Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Setup Group Confirmation */}
      <AlertDialog open={!!deleteSetupGroupId} onOpenChange={() => setDeleteSetupGroupId(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Delete Setup Group</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Delete this Setup Group? This will remove the group only. Your setups and trades will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-setup-group"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-setup-group"
              onClick={() => handleDeleteSetupGroup(deleteSetupGroupId)}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      <ShareModal 
        open={showShareModal} 
        onOpenChange={setShowShareModal} 
        defaultTitle="My Trading Analytics"
      />

      {/* Import Paywall Modal - Value Proposition (for non-yearly users) */}
      <ImportPaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        onUpgradeClick={handlePaywallUpgradeClick}
      />

      {/* Payment Modal - After clicking upgrade in paywall */}
      <CryptoPaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setIsUpgradeFlow(false);
        }}
        onPaymentSubmitted={() => {
          setShowPaymentModal(false);
          setIsUpgradeFlow(false);
          fetchProStatus();
        }}
        initialPlan="yearly"
        isUpgrade={isUpgradeFlow}
        upgradePrice={79}
        onBack={() => {
          setShowPaymentModal(false);
          setShowPaywallModal(true);
        }}
      />
      
      {/* Delete All Data - First Warning Modal */}
      <AlertDialog open={showDeleteAllWarning} onOpenChange={setShowDeleteAllWarning}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading text-xl">
              Delete all trading data?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 space-y-4">
              <p>
                You are about to permanently delete <span className="text-white font-medium">ALL trades</span> you have logged.
              </p>
              <p>
                After this, your account will feel like a fresh start and your analytics will return to zero.
              </p>
              <p className="text-[#FFC107] text-sm">
                Most traders need at least 50 trades to properly evaluate a setup across different market conditions.
              </p>
              <p className="text-white/50 text-sm">
                By deleting everything, you are resetting your progress toward discovering what actually works for you.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel 
              className="bg-white/10 text-white border-white/10 hover:bg-white/20"
              autoFocus
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setShowDeleteAllWarning(false);
                setShowDeleteAllConfirm(true);
              }}
              className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
            >
              Delete anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete All Data - Final Typed Confirmation Modal */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={(open) => {
        setShowDeleteAllConfirm(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              Final confirmation required
            </DialogTitle>
            <DialogDescription className="text-white/60 space-y-4">
              <p className="text-red-400 font-medium">
                This action cannot be undone.
              </p>
              <p>
                To confirm permanent deletion of all your trading data, type exactly:
              </p>
              <p className="text-white font-mono text-lg tracking-wider text-center py-2 bg-black/30 rounded border border-white/10">
                DELETEMYDATA
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETEMYDATA"
              className="bg-black border border-white/20 text-white font-mono text-center tracking-wider"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteAllConfirm(false);
                setDeleteConfirmText('');
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAllData}
              disabled={deleteConfirmText.trim() !== 'DELETEMYDATA' || deletingAllData}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="confirm-delete-all-btn"
            >
              {deletingAllData ? "Deleting..." : "Permanently delete all data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer Branding */}
      <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
        <img
          src={LOGO_PUREBRAIN}
          alt="GENI"
          className="h-5 w-auto object-contain opacity-80"
        />
        <span>
          Powered by <span className="text-white font-medium">GENI</span>
        </span>
      </div>
    </div>
  );
}
